// regrasDeSaneamento.ts
//
// A travessia do cardápio e os detectores de problema. Funções PURAS: recebem o
// `decryptedInfoToken` (ou a travessia já pronta) e devolvem listas. Nada aqui
// faz requisição, nada aqui grava — a aba Cardápio é só leitura.
//
// POR QUE O NOME NÃO É `saneamento.ts`: o macOS é case-insensitive, e um arquivo
// `saneamento.ts` ao lado de `Saneamento.tsx` é o MESMO caminho para o
// resolvedor. `import ... from "./Saneamento"` cairia no módulo de regras (erro
// TS1149 + TS1192 no `tsc`, e o CaseSensitivePathsPlugin do react-scripts
// derruba o `npm start`). Os dois nomes não podem coexistir na mesma pasta.
//
// ONDE O CARDÁPIO MORA (conferido no token real de `src/store/exemplo-de-decryptedInfoToken.json`)
// ------------------------------------------------------------------------------
//   organization.companies[].companyInfo[] com companyInfoType === 1
//       → uma linha POR CATEGORIA. O nome da categoria está em `companyInfoName`
//         (`companyInfoDescription` vem string vazia em todas as 21 linhas da
//         rede real). O `companyInfoValue` é um JSON `{ products: [...] }` e é
//         o ÚNICO lugar onde existe preço.
//   organization.organizationInfo[]
//       → os produtos soltos (504 na rede real). Aqui `organizationInfoName` é
//         o CÓDIGO ("197") e `organizationInfoDescription` é o NOME humano
//         ("*SMIRNOFF") — o inverso do que o nome do campo sugere, e a armadilha
//         que derruba quem lê só o "Name". Não há preço nem unidade: o valor é
//         `{ "productServiceTax": bool, "productCombo": bool }`.
//
// A JUNÇÃO é companyInfoValue.products[].productUuid === organizationInfoUuid.
//
// Todo JSON.parse aqui está em try/catch devolvendo objeto vazio: um único
// valor malformado no meio de 504 registros derrubaria a aba inteira, e a tela
// que existe para MOSTRAR sujeira não pode ser a primeira vítima dela.

/** Uma linha de problema, sempre nominal — o dono precisa saber QUAL registro. */
export interface RegistroCardapio {
  /** Chave estável de render (uuid quando existe; senão código + nome). */
  chave: string;
  codigo: string;
  nome: string;
  /** Contexto curto: categoria, preço, produto irmão. */
  detalhe?: string;
}

export interface ProdutoDeCategoria {
  productUuid: string;
  productName: string;
  /** `null` quando o JSON não trouxe número — não vira 0, que seria inventar. */
  productPrice: number | null;
  /** Não existe no token real; o legado às vezes manda "Erro" aqui. */
  productType: string | null;
}

export interface CategoriaCardapio {
  uuid: string;
  nome: string;
  empresa: string;
  produtos: ProdutoDeCategoria[];
  /** `companyInfoValue` não era JSON válido, ou não tinha `products`. */
  valorIlegivel: boolean;
}

export interface ProdutoCardapio {
  uuid: string;
  /** organizationInfoName — o código do PDV. */
  codigo: string;
  /** organizationInfoDescription. Cai para o código quando vem vazio. */
  nome: string;
  /** Nomes das categorias em que o produto aparece. Vazio = órfão. */
  categorias: string[];
  /** O preço é o da categoria; produto fora de categoria não tem preço nenhum. */
  preco: number | null;
  combo: boolean;
  taxaServico: boolean;
  valorIlegivel: boolean;
  ativo: boolean;
}

export interface Cardapio {
  categorias: CategoriaCardapio[];
  produtos: ProdutoCardapio[];
  /** Produtos citados dentro de uma categoria que não têm cadastro no organizationInfo. */
  citadosSemCadastro: RegistroCardapio[];
}

export type GravidadeProblema = "alta" | "media" | "baixa";

export interface ProblemaCardapio {
  chave: string;
  titulo: string;
  /** Ligatura do Material Icons. */
  icone: string;
  gravidade: GravidadeProblema;
  /** O que isso quebra na prática, na loja — não a definição do defeito. */
  oQueQuebra: string;
  /** Onde se conserta de verdade. A Central não edita cardápio. */
  ondeSeResolve: string;
  registros: RegistroCardapio[];
}

/** O tipo é o do legado: 1 = linha de cardápio, tanto em companyInfo quanto em organizationInfo. */
const INFO_TYPE_CARDAPIO = 1;

/**
 * Não derruba a tela por um valor sujo. Devolve o objeto E se ele deu para ler,
 * porque as duas coisas são diferentes: `{}` de "veio vazio" não é defeito,
 * `{}` de "não era JSON" é — e a tela precisa saber qual dos dois aconteceu.
 */
function lerJson(bruto: unknown): { valor: any; ilegivel: boolean } {
  if (typeof bruto !== "string" || bruto.trim() === "")
    return { valor: {}, ilegivel: false };
  try {
    const valor = JSON.parse(bruto);
    return valor && typeof valor === "object"
      ? { valor, ilegivel: false }
      : { valor: {}, ilegivel: true };
  } catch {
    return { valor: {}, ilegivel: true };
  }
}

function texto(valor: unknown): string {
  return typeof valor === "string" ? valor.trim() : "";
}

/** Preço só vira número quando é número de verdade; "" e null continuam `null`. */
function numeroOuNulo(valor: unknown): number | null {
  if (typeof valor === "number" && isFinite(valor)) return valor;
  if (typeof valor === "string" && valor.trim() !== "") {
    const convertido = Number(valor.replace(",", "."));
    return isFinite(convertido) ? convertido : null;
  }
  return null;
}

export function formatarReais(valor: number | null): string {
  if (valor === null) return "sem preço";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

/**
 * A travessia. Devolve as categorias com seus produtos e os produtos com a
 * categoria/preço já resolvidos pela junção do uuid.
 */
export function lerCardapio(decryptedInfoToken: any): Cardapio {
  const organizacao = decryptedInfoToken?.organization;
  const empresas: any[] = Array.isArray(organizacao?.companies)
    ? organizacao.companies
    : [];

  const categorias: CategoriaCardapio[] = [];

  for (const empresa of empresas) {
    const linhas: any[] = Array.isArray(empresa?.companyInfo)
      ? empresa.companyInfo
      : [];
    for (const linha of linhas) {
      if (Number(linha?.companyInfoType) !== INFO_TYPE_CARDAPIO) continue;

      const { valor, ilegivel } = lerJson(linha?.companyInfoValue);
      const brutos: any[] = Array.isArray(valor?.products) ? valor.products : [];

      categorias.push({
        uuid: texto(linha?.companyInfoUuid),
        // O nome da categoria vem no "Name"; a "Description" está vazia em todas
        // as 21 linhas da rede real, então não adianta cair nela.
        nome: texto(linha?.companyInfoName) || "(categoria sem nome)",
        empresa: texto(empresa?.companyName),
        valorIlegivel: ilegivel,
        produtos: brutos.map((produto: any) => ({
          productUuid: texto(produto?.productUuid),
          productName: texto(produto?.productName),
          productPrice: numeroOuNulo(produto?.productPrice),
          productType: texto(produto?.productType) || null,
        })),
      });
    }
  }

  // Índice uuid -> {categorias, preço}, montado uma vez para não varrer as 21
  // categorias dentro do laço dos 504 produtos.
  const porUuid: Record<string, { categorias: string[]; preco: number | null }> =
    {};
  for (const categoria of categorias) {
    for (const produto of categoria.produtos) {
      if (!produto.productUuid) continue;
      const atual = porUuid[produto.productUuid] || {
        categorias: [],
        preco: null,
      };
      atual.categorias.push(categoria.nome);
      if (atual.preco === null) atual.preco = produto.productPrice;
      porUuid[produto.productUuid] = atual;
    }
  }

  const linhasProduto: any[] = Array.isArray(organizacao?.organizationInfo)
    ? organizacao.organizationInfo
    : [];

  const cadastrados: Record<string, true> = {};
  const produtos: ProdutoCardapio[] = [];

  for (const linha of linhasProduto) {
    if (Number(linha?.organizationInfoType) !== INFO_TYPE_CARDAPIO) continue;

    const uuid = texto(linha?.organizationInfoUuid);
    const { valor, ilegivel } = lerJson(linha?.organizationInfoValue);
    const juncao = porUuid[uuid] || { categorias: [], preco: null };
    const codigo = texto(linha?.organizationInfoName);

    cadastrados[uuid] = true;
    produtos.push({
      uuid,
      codigo,
      // O nome humano mora na Description. Sem ela, o código é o único
      // identificador que sobra — melhor mostrá-lo do que uma linha em branco.
      nome: texto(linha?.organizationInfoDescription) || codigo,
      categorias: juncao.categorias,
      preco: juncao.preco,
      combo: valor?.productCombo === true,
      taxaServico: valor?.productServiceTax === true,
      valorIlegivel: ilegivel,
      ativo: linha?.organizationInfoStatus !== false,
    });
  }

  // O caminho inverso da junção: a categoria cita um produto que não existe no
  // cadastro. É o que faz o PDV mostrar item que não dá para vender.
  const citadosSemCadastro: RegistroCardapio[] = [];
  const jaVistos: Record<string, true> = {};
  for (const categoria of categorias) {
    for (const produto of categoria.produtos) {
      const uuid = produto.productUuid;
      if (!uuid || cadastrados[uuid] || jaVistos[uuid]) continue;
      jaVistos[uuid] = true;
      citadosSemCadastro.push({
        chave: uuid,
        codigo: "—",
        nome: produto.productName || uuid,
        detalhe: `citado em "${categoria.nome}", sem cadastro de produto`,
      });
    }
  }

  return { categorias, produtos, citadosSemCadastro };
}

/**
 * Índice uuid -> código do PDV. Dentro da categoria o item só tem uuid, e uuid
 * não serve para achar o produto no OPS: quem vai limpar procura pelo CÓDIGO.
 */
function codigosPorUuid(cardapio: Cardapio): Record<string, string> {
  const indice: Record<string, string> = {};
  for (const produto of cardapio.produtos) {
    if (produto.uuid) indice[produto.uuid] = produto.codigo;
  }
  return indice;
}

function registroDoProduto(
  produto: ProdutoCardapio,
  detalhe?: string
): RegistroCardapio {
  return {
    chave: produto.uuid || `${produto.codigo}-${produto.nome}`,
    codigo: produto.codigo || "—",
    nome: produto.nome,
    detalhe,
  };
}

// ============================================================================
// DETECTORES
// Cada um devolve o problema COM A LISTA. Contagem sem nome não conserta nada:
// quem vai limpar precisa do código para achar o item no OPS.
// ============================================================================

export function codigosDuplicados(cardapio: Cardapio): ProblemaCardapio {
  const porCodigo: Record<string, ProdutoCardapio[]> = {};
  for (const produto of cardapio.produtos) {
    const codigo = produto.codigo;
    if (!codigo) continue;
    (porCodigo[codigo] = porCodigo[codigo] || []).push(produto);
  }

  const registros: RegistroCardapio[] = [];
  for (const codigo of Object.keys(porCodigo)) {
    const grupo = porCodigo[codigo];
    if (grupo.length < 2) continue;
    for (const produto of grupo) {
      registros.push(
        registroDoProduto(
          produto,
          `código ${codigo} repetido em ${grupo.length} produtos`
        )
      );
    }
  }

  return {
    chave: "codigosDuplicados",
    titulo: "Códigos repetidos",
    icone: "content_copy",
    gravidade: "alta",
    oQueQuebra:
      "O código é o que o operador digita no PDV e o que o relatório agrupa. " +
      "Com dois produtos no mesmo código, a venda de um aparece somada à do " +
      "outro e não há como saber qual saiu.",
    ondeSeResolve:
      "No OPS, renumerando um dos produtos. A Central não edita cadastro de produto.",
    registros,
  };
}

export function nomesDuplicados(cardapio: Cardapio): ProblemaCardapio {
  const porNome: Record<string, ProdutoCardapio[]> = {};
  for (const produto of cardapio.produtos) {
    const chave = produto.nome.toUpperCase();
    if (!chave) continue;
    (porNome[chave] = porNome[chave] || []).push(produto);
  }

  const registros: RegistroCardapio[] = [];
  for (const chave of Object.keys(porNome)) {
    const grupo = porNome[chave];
    if (grupo.length < 2) continue;
    for (const produto of grupo) {
      registros.push(
        registroDoProduto(
          produto,
          `mesmo nome em ${grupo.length} produtos — ${formatarReais(
            produto.preco
          )}${produto.categorias.length ? ` em ${produto.categorias[0]}` : ""}`
        )
      );
    }
  }

  return {
    chave: "nomesDuplicados",
    titulo: "Nomes repetidos com códigos diferentes",
    icone: "join_left",
    gravidade: "media",
    oQueQuebra:
      "Na tela do PDV o operador vê duas linhas idênticas com preços que podem " +
      "divergir, e escolhe no chute. No relatório, a venda do mesmo item fica " +
      "partida em dois códigos.",
    ondeSeResolve:
      "No OPS: manter um e desativar o outro, ou diferenciar o nome.",
    registros,
  };
}

/**
 * "IF " é o prefixo com que a rede duplicou o cardápio para o iFood, por falta
 * de preço por canal. Procura no NOME e no CÓDIGO: na rede real o prefixo está
 * no `organizationInfoDescription` (o nome), mas o campo que carrega o texto
 * varia por carga e olhar só um dos dois deixaria o problema invisível.
 */
export function prefixoIF(cardapio: Cardapio): ProblemaCardapio {
  const precoPorNome: Record<string, ProdutoCardapio> = {};
  for (const produto of cardapio.produtos) {
    precoPorNome[produto.nome.toUpperCase()] = produto;
  }

  const registros: RegistroCardapio[] = [];
  for (const produto of cardapio.produtos) {
    const nome = produto.nome.toUpperCase();
    const codigo = produto.codigo.toUpperCase();
    if (!nome.startsWith("IF ") && !codigo.startsWith("IF ")) continue;

    // O par só existe quando é o NOME que carrega o prefixo. Quando quem tem
    // "IF " é o código, cortar 3 letras do nome produziria uma chave qualquer,
    // que pode casar por acidente com outro produto — e a tela mostraria uma
    // comparação de preço entre dois itens que não têm relação nenhuma.
    const base = nome.startsWith("IF ")
      ? precoPorNome[nome.slice(3).trim()]
      : undefined;
    const detalhe = base
      ? `${formatarReais(produto.preco)} — o item sem "IF" (${
          base.codigo
        }) custa ${formatarReais(base.preco)}`
      : `${formatarReais(produto.preco)} — não há item equivalente sem "IF"`;

    registros.push(registroDoProduto(produto, detalhe));
  }

  return {
    chave: "prefixoIF",
    titulo: 'Produtos duplicados com o prefixo "IF"',
    icone: "call_split",
    gravidade: "alta",
    oQueQuebra:
      "São cópias do mesmo item criadas só para ter outro preço no iFood. " +
      "Dobram o cardápio, dobram o trabalho de qualquer alteração de preço e " +
      "partem o faturamento do item em dois códigos no relatório.",
    ondeSeResolve:
      "Depende de preço por canal existir no dado. Enquanto não existir, a " +
      "duplicação é a única forma que a rede tem — ver a sub-tela Preço por canal.",
    registros,
  };
}

/**
 * "#ERROR!" é o rastro de planilha: alguém montou a carga no Excel, a fórmula
 * quebrou e o texto do erro entrou como se fosse dado. Varre código, nome e o
 * nome da categoria.
 */
export function valoresDeErro(cardapio: Cardapio): ProblemaCardapio {
  const MARCA = "#ERROR!";
  const registros: RegistroCardapio[] = [];

  for (const produto of cardapio.produtos) {
    const ondeAparece: string[] = [];
    if (produto.codigo.toUpperCase().indexOf(MARCA) >= 0)
      ondeAparece.push("código");
    if (produto.nome.toUpperCase().indexOf(MARCA) >= 0) ondeAparece.push("nome");
    if (ondeAparece.length === 0) continue;
    registros.push(
      registroDoProduto(produto, `#ERROR! no ${ondeAparece.join(" e no ")}`)
    );
  }

  for (const categoria of cardapio.categorias) {
    if (categoria.nome.toUpperCase().indexOf(MARCA) >= 0) {
      registros.push({
        chave: categoria.uuid || categoria.nome,
        codigo: "categoria",
        nome: categoria.nome,
        detalhe: "#ERROR! no nome da categoria",
      });
    }
    for (const produto of categoria.produtos) {
      if (produto.productName.toUpperCase().indexOf(MARCA) >= 0) {
        registros.push({
          chave: `${categoria.uuid}-${produto.productUuid}`,
          codigo: "—",
          nome: produto.productName,
          detalhe: `#ERROR! no nome dentro da categoria "${categoria.nome}"`,
        });
      }
    }
  }

  return {
    chave: "valoresDeErro",
    titulo: "Texto de erro de planilha (#ERROR!) virou cadastro",
    icone: "report",
    gravidade: "alta",
    oQueQuebra:
      "O cliente lê #ERROR! no cardápio do app e na comanda impressa. É o dado " +
      "de uma célula que falhou na planilha de carga e entrou assim mesmo.",
    ondeSeResolve:
      "No OPS, corrigindo o cadastro — e refazendo a carga que trouxe o texto.",
    registros,
  };
}

/** Nome com "teste"/"test": item que nasceu para conferir a carga e ficou vendável. */
export function skusDeTeste(cardapio: Cardapio): ProblemaCardapio {
  const ehTeste = (valor: string) => /\btest(e|es|s)?\b/i.test(valor);
  const registros: RegistroCardapio[] = [];

  for (const produto of cardapio.produtos) {
    if (!ehTeste(produto.nome) && !ehTeste(produto.codigo)) continue;
    registros.push(
      registroDoProduto(
        produto,
        `${formatarReais(produto.preco)}${
          produto.categorias.length
            ? ` em ${produto.categorias.join(", ")}`
            : " — fora de categoria"
        }`
      )
    );
  }

  return {
    chave: "skusDeTeste",
    titulo: "Produtos de teste no cardápio de produção",
    icone: "science",
    gravidade: "media",
    oQueQuebra:
      "Item de teste dentro de uma categoria aparece para o cliente no app e " +
      "para o operador no PDV. Vendido, entra no faturamento como produto real.",
    ondeSeResolve: "No OPS, desativando o produto.",
    registros,
  };
}

/**
 * Preço de 1 centavo (ou menos) é o jeito de "esconder" um item sem desativá-lo.
 * A varredura pega `<= 0.01`, então zero e valor negativo entram aqui também —
 * por isso o título diz "ou menos", e o detalhe de cada linha traz o valor real.
 */
export function itensDeUmCentavo(cardapio: Cardapio): ProblemaCardapio {
  const codigos = codigosPorUuid(cardapio);
  const registros: RegistroCardapio[] = [];

  for (const categoria of cardapio.categorias) {
    for (const produto of categoria.produtos) {
      if (produto.productPrice === null || produto.productPrice > 0.01) continue;
      registros.push({
        chave: `${categoria.uuid}-${produto.productUuid}`,
        codigo: codigos[produto.productUuid] || "—",
        nome: produto.productName,
        detalhe: `${formatarReais(produto.productPrice)} em "${categoria.nome}"`,
      });
    }
  }

  return {
    chave: "itensDeUmCentavo",
    titulo: "Itens a R$ 0,01 ou menos",
    icone: "money_off",
    gravidade: "alta",
    oQueQuebra:
      "Um centavo é preço de verdade: o item vende por esse valor se alguém tocar " +
      "nele no PDV ou no app. Costuma ser item que deveria estar desativado, ou " +
      "acompanhamento que perdeu o preço na carga.",
    ondeSeResolve:
      "No OPS: colocar o preço certo, ou desativar o item se ele não é vendável.",
    registros,
  };
}

/**
 * `productType: "Erro"` dentro do JSON da categoria. O campo NÃO existe no token
 * real desta rede (todos vêm sem `productType`), mas o legado o emite em cargas
 * que falharam — por isso a varredura fica, e uma lista vazia aqui é informação:
 * quer dizer que esta carga não trouxe combo quebrado.
 */
export function combosComTipoErro(cardapio: Cardapio): ProblemaCardapio {
  const codigos = codigosPorUuid(cardapio);
  const registros: RegistroCardapio[] = [];

  for (const categoria of cardapio.categorias) {
    for (const produto of categoria.produtos) {
      if ((produto.productType || "").toUpperCase() !== "ERRO") continue;
      registros.push({
        chave: `${categoria.uuid}-${produto.productUuid}`,
        codigo: codigos[produto.productUuid] || "—",
        nome: produto.productName,
        detalhe: `productType "Erro" em "${categoria.nome}"`,
      });
    }
  }

  return {
    chave: "combosComTipoErro",
    titulo: 'Combos marcados com tipo "Erro"',
    icone: "dangerous",
    gravidade: "alta",
    oQueQuebra:
      "O tipo do item veio como \"Erro\" da carga. O PDV não sabe montar o combo " +
      "e o item ou some da tela ou entra sem os componentes.",
    ondeSeResolve: "No OPS, refazendo o cadastro do combo.",
    registros,
  };
}

/** Produto cadastrado que não aparece em nenhuma categoria: sem categoria, sem preço. */
export function produtosOrfaos(cardapio: Cardapio): ProblemaCardapio {
  const registros = cardapio.produtos
    .filter((produto) => produto.categorias.length === 0)
    .map((produto) =>
      registroDoProduto(
        produto,
        produto.combo ? "marcado como combo" : "sem categoria e sem preço"
      )
    );

  return {
    chave: "produtosOrfaos",
    titulo: "Produtos fora de qualquer categoria",
    icone: "link_off",
    gravidade: "media",
    oQueQuebra:
      "O preço só existe dentro da categoria. Produto que não está em nenhuma " +
      "não tem preço nenhum e não aparece no cardápio do app — mas continua " +
      "cadastrado, ocupando código e podendo ser chamado pelo PDV.",
    ondeSeResolve:
      "No OPS: colocar o produto numa categoria com preço, ou desativá-lo.",
    registros,
  };
}

/** Categoria sem produto — e também a que veio com o JSON ilegível. */
export function categoriasVazias(cardapio: Cardapio): ProblemaCardapio {
  const registros = cardapio.categorias
    .filter(
      (categoria) => categoria.valorIlegivel || categoria.produtos.length === 0
    )
    .map((categoria) => ({
      chave: categoria.uuid || categoria.nome,
      codigo: categoria.empresa || "—",
      nome: categoria.nome,
      detalhe: categoria.valorIlegivel
        ? "o valor gravado não é uma lista de produtos legível"
        : "nenhum produto",
    }));

  return {
    chave: "categoriasVazias",
    titulo: "Categorias sem produto",
    icone: "folder_off",
    gravidade: "baixa",
    oQueQuebra:
      "Aparece como aba vazia no app e como grupo vazio no PDV. Quem abre acha " +
      "que o sistema falhou em carregar.",
    ondeSeResolve: "No OPS: preencher a categoria ou removê-la da carga.",
    registros,
  };
}

/**
 * Todos os detectores, na ordem em que a tela mostra: o que quebra dinheiro
 * primeiro. A ordem NÃO é por contagem — 3 itens a um centavo são mais urgentes
 * do que 192 duplicatas conhecidas e intencionais.
 */
export function detectarProblemas(cardapio: Cardapio): ProblemaCardapio[] {
  return [
    valoresDeErro(cardapio),
    itensDeUmCentavo(cardapio),
    codigosDuplicados(cardapio),
    combosComTipoErro(cardapio),
    prefixoIF(cardapio),
    nomesDuplicados(cardapio),
    skusDeTeste(cardapio),
    produtosOrfaos(cardapio),
    categoriasVazias(cardapio),
  ];
}

export interface ResumoCardapio {
  categorias: number;
  produtos: number;
  produtosComPreco: number;
  produtosSemCategoria: number;
  produtosCombo: number;
  categoriasIlegiveis: number;
  citadosSemCadastro: number;
  problemasComOcorrencia: number;
  registrosComProblema: number;
}

/** Os números do topo da Visão geral. Contados no dado, nunca cravados. */
export function resumirCardapio(
  cardapio: Cardapio,
  problemas: ProblemaCardapio[]
): ResumoCardapio {
  const comOcorrencia = problemas.filter((p) => p.registros.length > 0);
  return {
    categorias: cardapio.categorias.length,
    produtos: cardapio.produtos.length,
    produtosComPreco: cardapio.produtos.filter((p) => p.preco !== null).length,
    produtosSemCategoria: cardapio.produtos.filter(
      (p) => p.categorias.length === 0
    ).length,
    produtosCombo: cardapio.produtos.filter((p) => p.combo).length,
    categoriasIlegiveis: cardapio.categorias.filter((c) => c.valorIlegivel)
      .length,
    citadosSemCadastro: cardapio.citadosSemCadastro.length,
    problemasComOcorrencia: comOcorrencia.length,
    registrosComProblema: comOcorrencia.reduce(
      (total, problema) => total + problema.registros.length,
      0
    ),
  };
}

/**
 * O aviso que aparece no topo de TODAS as sub-telas de Cardápio. Fica aqui, em
 * um lugar só, porque é uma promessa que a aba faz ao dono da rede — se mudar,
 * muda em todas ao mesmo tempo.
 */
export const AVISOS_SOMENTE_LEITURA: string[] = [
  "Esta aba é somente leitura: não existe botão de salvar em nenhuma das sub-telas.",
  "A edição de preço, de produto e de categoria continua no OPS. A carga é do OPS para a Central — a Central não devolve cardápio para o PDV.",
  'Por isso esta aba não promete "aplicado em N de M lojas" para cardápio: ela mostra o que chegou na última carga, e só.',
];

/** Categorias e Grupos de SUPRIMENTOS são outra coisa, e não são duplicadas aqui. */
export const ONDE_FICAM_CATEGORIAS_E_GRUPOS =
  'As Categorias e os Grupos de insumo ficam em Suprimentos → "Categorias" e ' +
  '"Grupos", e não são repetidos aqui. As categorias mostradas nesta aba são as ' +
  "do CARDÁPIO (as que agrupam os produtos vendidos e carregam o preço), que são " +
  "outra tabela.";
